import SectionWrapper from '@/components/_rebrand/craft/SectionWrapper';
import { StyleOverride } from '@/types/craft-style-overrides';
import cx from 'classnames';
import Image from 'next/image';
import { FC } from 'react';
import { AwardBadges } from './AwardBadges';
import { TileBreakup } from './TileBreakup';

export type ProductAwardsProps = {
  hideComponent?: boolean;
  title: React.ReactNode;
  description: string | JSX.Element;
  image: React.ReactNode;
  featuredAwards?: {
    icon: React.ReactNode,
    title: string;
    description: string | JSX.Element;
  }[];
  showTileBreakup?: boolean;
  awardBadges: React.ReactNode[];
  styleOverrides?: StyleOverride;
  containerBackgroundColor?: string;
}

const ProductAwards: FC<ProductAwardsProps> = ({
  hideComponent,
  title,
  description,
  featuredAwards,
  image,
  awardBadges = [],
  showTileBreakup = true,
  styleOverrides,
  containerBackgroundColor = 'bg-white-100'
}) => {
  return (
    <SectionWrapper styleOverrides={styleOverrides} className="">
      <div
        className={cx(
          hideComponent ? 'hidden' : 'flex',
          containerBackgroundColor, 'px-3', !styleOverrides ? 'py-6' : 'py-1', 'md:px-container-desktop', !styleOverrides ? 'md:pt-12 pb-16 md:pb-20' : '')}
      >
        <div className="max-w-grid mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-gutter-mobile md:gap-x-gutter-desktop gap-y-3">
            <div className="lg:col-span-6 flex flex-col gap-3 md:gap-6 relative">
              <div className="flex flex-col gap-3">
                <p className="text-30/36 md:text-36/42 font-500 tracking-tight text-blue-900">{title}</p>
                <div className="text-17/24 text-blue-1000 lg:pr-8">{description}</div>
              </div>
              {!styleOverrides &&
                <div className="flex w-full md:w-[90%] border-[1px] border-solid border-gray-200"></div>
              }
              <div className="flex flex-col gap-3 items-start">
                {featuredAwards ? (
                  featuredAwards.map((featuredAward, index) => (
                    <div className="flex relative items-center rounded-40" key={`featured-award-${index}`}>
                      <div className="flex bg-white p-[8px] rounded-[8px]">
                        <div className="text-12/16 p-1">{featuredAward.icon}</div>
                      </div>
                      <div className="flex flex-wrap ml-[16px]">
                        <div className="text-caption1 md:text-p2 pr-2 text-gray-500 font-500 w-full">
                          {featuredAward.title}
                        </div>
                        <div className="text-caption1 md:text-p2 pr-0 md:pr-2 text-blue-900 font-500 w-full">
                          {featuredAward.description}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex relative items-center rounded-40">
                    <div className="flex bg-white p-[8px] rounded-[8px]">
                      <Image
                        src="https://front.com/webassets/awards/front-capterra-customer-service-shortlist-2026.svg"
                        alt="Capterra Customer Service Shortlist 2026"
                        width={80}
                        height={80}
                      />
                    </div>
                    <div className="flex flex-wrap ml-[16px]">
                      <div className="text-caption1 md:text-p2 pr-2 text-gray-500 font-500 w-full">2026</div>
                      <div className="text-caption1 md:text-p2 pr-0 md:pr-2 text-blue-900 font-500 w-full">
                        Capterra Customer Service Shortlist
                      </div>
                    </div>
                  </div>
                )}
                <div className="relative 2xl:absolute bottom-0">
                  <div
                    className="hidden md:flex items-center justify-between gap-2 xl:pr-8 border-2 border-gray-700 relative bottom-0 w-full">
                    <AwardBadges awardBadges={awardBadges} prefix="side-top" />
                  </div>
                </div>
              </div>

            </div>
            <div className="lg:col-span-6">
              <div className="overflow-hidden rounded-10">{image}</div>
              <div
                className="flex md:hidden mt-[50px] items-center justify-between gap-2 xl:pr-8 border-2 border-gray-700 relative bottom-0 w-full">
                <AwardBadges awardBadges={awardBadges} prefix="below" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {showTileBreakup && (
        <TileBreakup />)}
    </SectionWrapper>
  );
};

export default ProductAwards;
